import i18n from "src/i18n/config";
import { z } from "zod";

import { SimplePolicyRule } from "../types";
import { requiredMessage, toNumber } from "../utils";

const timeoutSetting = { command: "changeSetting", section: "System", type: "int", key: "screen_off_timeout" };
const neverTimeout = "2147483647";
const minuteInMs = 60_000;
const maxMinutes = Math.floor(2_147_483_647 / 60_000);

const screenTimeoutSchema = z
    .object({ never: z.boolean(), minutes: z.number().int().min(1).max(maxMinutes).optional() })
    .superRefine(({ never, minutes }, context) => {
        if (!never && minutes === undefined) {
            context.addIssue({ code: "custom", path: ["minutes"], message: requiredMessage() });
        }
    });

function fromTimeout(value: unknown) {
    if (value === neverTimeout) {
        return { never: true };
    }
    const timeout = toNumber(value);
    return timeout !== undefined && timeout % minuteInMs === 0 ? { never: false, minutes: timeout / minuteInMs } : null;
}

export const screenTimeout: SimplePolicyRule<z.infer<typeof screenTimeoutSchema>> = {
    id: "screenTimeout",
    title: () => i18n.t("modals:addRule.rules.screenTimeout.title"),
    description: () => i18n.t("modals:addRule.rules.screenTimeout.description"),
    summary: (display, { never, minutes }) =>
        never
            ? i18n.t("modals:addRule.rules.screenTimeout.summaryNever")
            : i18n.t("modals:addRule.rules.screenTimeout.summary", { ...display, count: minutes }),
    categories: ["display"],
    fields: [
        { type: "boolean", name: "never", label: () => i18n.t("modals:addRule.rules.screenTimeout.never") },
        {
            type: "numeric",
            name: "minutes",
            label: () => i18n.t("modals:addRule.rules.screenTimeout.minutes"),
            min: 1,
            max: maxMinutes,
            step: 1,
            dependsOn: { name: "never", value: false },
        },
    ],
    defaultValues: { never: false, minutes: 30 },
    schema: screenTimeoutSchema,
    command: timeoutSetting,
    toValues: ({ never, minutes }) => (never || minutes === undefined ? neverTimeout : String(minutes * minuteInMs)),
    fromValues: fromTimeout,
    flags: [],
};
